import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAdminActiveUsersStats } from "../../../utils/Api";

interface DataPoint {
  label: string;
  value: number;
  key: string;
  fill?: string;
}

interface StatsResponse {
  total_users: number;
  data: DataPoint[];
}

const COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];

const ActiveUsersChart: React.FC = () => {
  const [data, setData] = useState<DataPoint[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAdminActiveUsersStats();
        const stats: StatsResponse = response.data;
        const dataWithColors: DataPoint[] = stats.data.map((item, index) => ({
          label: item.label,
          value: item.value,
          key: item.key,
          fill: COLORS[index % COLORS.length] ?? "#6b7280",
        }));
        setData(dataWithColors);
        setTotalUsers(stats.total_users);
      } catch (err) {
        setError("Failed to load active user statistics");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-white rounded shadow p-4 sm:p-6 h-full">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Active Users</h2>
        <p className="text-sm text-gray-500">
          Total users: {totalUsers.toLocaleString()}
        </p>
      </div>

      {loading ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : error ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 sm:h-80 flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      ) : (
        <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              width={100}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              formatter={(value) => [
                typeof value === "number" ? value.toLocaleString() : value,
                "Users",
              ]}
            />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ActiveUsersChart;
